/* eslint-disable no-console */

const TIMER_COUNTDOWN_DEFAULT_DEBUG = true;
let TIMER_COUNTDOWN_NEXT_ID = 1;

const TRUTHY_STRINGS = ['true', '1', 'yes', 'on'];

function parseBoolean(value) {
  if (value === undefined || value === null) return false;
  return TRUTHY_STRINGS.includes(String(value).toLowerCase());
}

function parseDateSafe(rawValue) {
  if (!rawValue) return null;
  const normalized = String(rawValue).replace(' ', 'T');
  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isDebugEnabled() {
  if (typeof window === 'undefined') {
    return TIMER_COUNTDOWN_DEFAULT_DEBUG;
  }

  if (window.__timerCountdownDebug === undefined) {
    return TIMER_COUNTDOWN_DEFAULT_DEBUG;
  }

  return Boolean(window.__timerCountdownDebug);
}

/**
 * Custom element that displays a countdown timer
 * @extends HTMLElement
 * @example
 * <timer-countdown
 *   data-start-date="2025-01-01 00:00:00"
 *   data-end-date="2025-12-31 23:59:59"
 *   data-reset-daily="true">
 *   <div><span data-timer-days>00</span><span>Days</span></div>
 *   <span>:</span>
 *   <div><span data-timer-hours>00</span><span>Hours</span></div>
 *   <span>:</span>
 *   <div><span data-timer-minutes>00</span><span>Minutes</span></div>
 *   <span>:</span>
 *   <div><span data-timer-seconds>00</span><span>Seconds</span></div>
 * </timer-countdown>
 */
class TimerCountdown extends HTMLElement {
  debug(event, details = {}) {
    if (!isDebugEnabled()) return;

    if (!this._debugLabel) {
      this._debugLabel = `TimerCountdown#${TIMER_COUNTDOWN_NEXT_ID++}`;
    }

    const payload = {
      ...details,
      element: this,
      dataset: { ...this.dataset },
      resetDaily: this.resetDaily,
      debugLabel: this._debugLabel,
    };

    try {
      console.groupCollapsed(`[${this._debugLabel}] ${event}`);
      console.debug(payload);
      console.groupEnd();
    } catch {
      // Ignore console errors
    }
  }

  /**
   * Called when element is connected to the DOM
   * Initializes the timer with the end date from data-end-date attribute
   */
  connectedCallback() {
    // Prevent multiple initialization
    if (this.initialized) return;
    this.initialized = true;

    // Initialize properties
    this.resetDaily = parseBoolean(this.dataset.resetDaily);
    this.endDate = parseDateSafe(this.dataset.endDate);
    this.startDate = parseDateSafe(this.dataset.startDate);
    this.interval = null;
    this.cachedMidnight = null;
    this.debug('connectedCallback:init', {
      raw: {
        start: this.dataset.startDate,
        end: this.dataset.endDate,
        resetDaily: this.dataset.resetDaily,
      },
      parsed: {
        start: this.startDate,
        end: this.endDate,
      },
    });

    // Validate dates with silent error handling
    // Validate dates with silent error handling
    if (!this.endDate && !this.resetDaily) {
      this.debug('connectedCallback:invalidEndDate', {
        endDate: this.dataset.endDate,
      });
      this.style.display = 'none';
      return;
    }

    if (this.startDate && this.endDate && this.startDate.getTime() > this.endDate.getTime()) {
      this.debug('connectedCallback:startAfterEnd', {
        startDate: this.startDate,
        endDate: this.endDate,
      });
      this.style.display = 'none';
      return;
    }

    if (!this.startDate) {
      this.startDate = null;
    }

    this.initElements();
    this.startTimer();
  }

  /**
   * Called when element is removed from the DOM
   * Cleans up the interval to prevent memory leaks
   */
  disconnectedCallback() {
    this.stopTimer();
    this.initialized = false;
  }

  /**
   * Caches DOM element references for performance
   * Sets up references to time display elements and days container
   * @private
   */
  initElements() {
    // Cache elements
    this.elements = {
      days: this.querySelector('[data-timer-days]'),
      daysContainer: this.querySelector('[data-timer-days-container]'),
      daysLabel: this.querySelector('[data-timer-days-label]'),
      hours: this.querySelector('[data-timer-hours]'),
      minutes: this.querySelector('[data-timer-minutes]'),
      seconds: this.querySelector('[data-timer-seconds]'),
    };
    this.debug('initElements', { hasElements: this.elements });

    // Cache the separator immediately following the days element (the first colon)
    this.daysSeparator = this.elements.days?.nextElementSibling;

    // Check if days should not be padded
    this.daysNoPad = this.elements.days?.hasAttribute('data-no-pad');
  }

  /**
   * Starts the countdown timer
   * Updates immediately and then every second
   * @private
   */
  startTimer() {
    this.debug('startTimer');
    this.updateTimer();
    this.interval = setInterval(() => this.updateTimer(), 1000);
  }

  /**
   * Updates the timer display
   * Calculates remaining time and updates UI
   * Stops timer when countdown reaches zero
   * @private
   */
  updateTimer() {
    const now = Date.now();
    this.debug('updateTimer:start', {
      now: new Date(now),
      startDate: this.startDate,
      endDate: this.endDate,
      resetDaily: this.resetDaily,
    });

    // Check if timer hasn't started yet - hide if start date is in future
    if (this.startDate && now < this.startDate.getTime()) {
      this.debug('updateTimer:beforeStart', {
        now: new Date(now),
        startDate: this.startDate,
      });
      this.style.display = 'none';
      return;
    } else if (this.startDate && this.style.display === 'none') {
      // Show timer when start date is reached
      this.debug('updateTimer:startReached', {
        now: new Date(now),
      });
      this.style.display = '';
    }

    // Ensure timer is visible while active
    this.style.display = '';
    this.style.visibility = 'visible';
    this.classList.remove('hidden', 'timer-expired');

    let targetDate = this.endDate;

    // Handle daily reset
    if (this.resetDaily) {
      const finalEndTime = this.endDate ? this.endDate.getTime() : null;

      if (finalEndTime && now > finalEndTime) {
        this.updateDisplay({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        this.debug('updateTimer:pastFinalEnd', {
          now: new Date(now),
          endDate: this.endDate,
        });
        this.stopTimer({ keepVisible: true });
        return;
      }

      targetDate = this.getNextMidnight(now);
      this.debug('updateTimer:resetDailyTarget', {
        targetDate,
        cachedMidnight: this.cachedMidnight,
      });
    }

    const distance = targetDate.getTime() - now;
    this.debug('updateTimer:distanceCalculated', {
      targetDate,
      distance,
    });

    if (distance < 0) {
      const withinFinalWindow = this.endDate ? now <= this.endDate.getTime() : true;

      if (this.resetDaily && withinFinalWindow) {
        // Daily countdown finished, show 00:00:00 until next tick
        this.updateDisplay({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        this.debug('updateTimer:resetDailyZeroGrace', {
          now: new Date(now),
          endDate: this.endDate,
        });
        return;
      }
      // Timer fully expired, show final state
      this.updateDisplay({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      this.debug('updateTimer:finished', {
        now: new Date(now),
        targetDate,
      });
      this.stopTimer();
      return;
    }

    const displayValues = this.calculateTimeValues(distance);
    this.updateDisplay(displayValues);
    this.debug('updateTimer:displayUpdated', {
      remaining: distance,
      display: displayValues,
    });
  }

  /**
   * Calculates time values from milliseconds
   * @param {number} distance - Time remaining in milliseconds
   * @returns {{days: number, hours: number, minutes: number, seconds: number}} Time values
   * @private
   */
  calculateTimeValues(distance) {
    const DAY = 86400000; // 24 * 60 * 60 * 1000
    const HOUR = 3600000; // 60 * 60 * 1000
    const MINUTE = 60000; // 60 * 1000
    const SECOND = 1000;

    return {
      days: Math.floor(distance / DAY),
      hours: Math.floor((distance % DAY) / HOUR),
      minutes: Math.floor((distance % HOUR) / MINUTE),
      seconds: Math.floor((distance % MINUTE) / SECOND),
    };
  }

  /**
   * Gets the next midnight date for daily reset functionality
   * @param {number} now - Current timestamp to optimize performance
   * @returns {Date} Next midnight date
   * @private
   */
  getNextMidnight(now) {
    if (!this.cachedMidnight || now >= this.cachedMidnight.getTime()) {
      const tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      this.cachedMidnight = tomorrow;
      this.debug('getNextMidnight:recalculated', {
        now: new Date(now),
        nextMidnight: this.cachedMidnight,
      });
    }
    this.debug('getNextMidnight:result', {
      now: new Date(now),
      nextMidnight: this.cachedMidnight,
    });
    return this.cachedMidnight;
  }

  /**
   * Updates the display with calculated time values
   * Hides days section when days reach zero
   * @param {{days: number, hours: number, minutes: number, seconds: number}} timeValues - Time values to display
   * @private
   */
  updateDisplay({ days, hours, minutes, seconds }) {
    try {
      // Update text content with error handling
      const pad = (n) => Math.max(0, Math.floor(n)).toString().padStart(2, '0');

      if (this.elements?.days) {
        // Use no-padding for days if data-no-pad attribute is set
        const daysValue = Math.max(0, Math.floor(days));
        this.elements.days.textContent = this.daysNoPad ? daysValue.toString() : pad(days);
        this.toggleDaysVisibility(days === 0);
      }

      if (this.elements?.hours) this.elements.hours.textContent = pad(hours);
      if (this.elements?.minutes) this.elements.minutes.textContent = pad(minutes);
      if (this.elements?.seconds) this.elements.seconds.textContent = pad(seconds);
    } catch {
      // Silently handle display update errors
    }
  }

  /**
   * Toggles visibility of days section and its separator
   * @param {boolean} hide - Whether to hide the days section
   * @private
   */
  toggleDaysVisibility(hide) {
    try {
      const action = hide ? 'add' : 'remove';

      // If there's a days container, toggle the entire container
      if (this.elements?.daysContainer) {
        this.elements.daysContainer.classList[action]('hidden');
      } else if (this.elements?.days) {
        // Fallback: toggle individual days element
        this.elements.days.classList[action]('hidden');
      }

      if (this.daysSeparator && this.daysSeparator.textContent.trim() === ':') {
        this.daysSeparator.classList[action]('hidden');
      }
    } catch {
      // Silently handle visibility toggle errors
    }
  }

  /**
   * Stops the timer and hides the element
   * Clears the interval and adds CSS classes for expired state
   * @private
   */
  stopTimer(options = {}) {
    const { keepVisible = false } = options;

    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
      this.debug('stopTimer:intervalCleared');
    }

    // Add expired state classes if they exist
    try {
      this.classList.add('timer-expired');
      if (keepVisible) {
        this.classList.remove('hidden');
      } else {
        this.classList.add('hidden');
      }
      this.debug('stopTimer:elementHidden', { keepVisible });
    } catch {
      // Silently handle classList errors
    }
  }
}

if (!customElements.get('timer-countdown')) {
  customElements.define('timer-countdown', TimerCountdown);
}
